import {
	AfterContentChecked,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
} from "@angular/core";
import {
	AbstractControl,
	FormBuilder,
	FormGroup,
	Validators,
} from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { forkJoin, lastValueFrom } from "rxjs";
import { CebeViewModel } from "src/app/_core/models/centros/cebe.model";
import {
	CentroActividadViewModel,
	LocalizacionViewModel,
	TipoInstalacionViewModel,
} from "src/app/_core/models/centros/centro-list-base.model";
import { CentrosViewModel } from "src/app/_core/models/centros/centro.model";
import { CebesService } from "src/app/_core/providers/cebes.service";
import { CentrosService } from "src/app/_core/providers/centros.service";
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";
import { SettingsService } from "src/app/_core/providers/settings.service";
import { CentroPageComponent } from "../../../pages/centro/centro.page";

@Component({
	selector: "app-centro-details-card",
	templateUrl: "./centro-details-card.component.html",
	styleUrls: ["./centro-details-card.component.scss"],
})
export class CentroDetailsCardComponent implements OnInit, AfterContentChecked {
	@Input() centro: CentrosViewModel;
	@Input() parent: CentroPageComponent;
	@Input() fotophotoResponsable: string | ArrayBuffer;

	@ViewChild("nombreInput", { static: true }) nombreInput: ElementRef;
	@ViewChild("photoEmResponsable", { static: true })
	photoEmResponsable: ElementRef;

	@Output() detailFormComplete = new EventEmitter<boolean>();
	@Output() centroDetailData = new EventEmitter<FormGroup>();

	public alltipoInstalacionList: TipoInstalacionViewModel[] = [];
	public tipoInstalacionList: TipoInstalacionViewModel[] = [];
	public centroActividadList: CentroActividadViewModel[] = [];
	public localizacionList: LocalizacionViewModel[] = [];

	isAdmin: boolean = false;
	cebe: CebeViewModel;

	myDetailForm: FormGroup;
	countControls: number = 0;
	countFilled: number = 0;
	imagenPreview: string | ArrayBuffer | null = null;
	imagenFile: File | null = null;

	constructor(
		private readonly _masterService: MasterService,
		private readonly _centrosService: CentrosService,
		private readonly _cebesService: CebesService,
		private readonly _notificationService: NotificationService,
		private readonly _translateService: TranslateService,
		public translate: TranslateService,
		private readonly _settingsService: SettingsService,
		private readonly fb: FormBuilder
	) {}

	// Events
	async ngOnInit(): Promise<void> {
		this._settingsService.isAdmin().subscribe((result) => {
			this.isAdmin = result;
		});

		this.loadCentroDetailsFormGroup();

		this.cebe = await lastValueFrom(
			this._cebesService.getCebeById(
				this.parent.cebeId != null && this.parent.cebeId !== 0
					? this.parent.cebeId
					: this.parent.cebeCentro.contratoID
			)
		);

		if (this.cebe?.paisID !== 65) {
			this.myDetailForm.get("ciudad").setValidators(Validators.required);
			this.myDetailForm.get("ciudad").updateValueAndValidity();

			this.myDetailForm
				.get("localizacionID")
				.removeValidators(Validators.required);
			this.myDetailForm.get("localizacionID").updateValueAndValidity();
		} else {
			this.myDetailForm
				.get("localizacionID")
				.setValidators(Validators.required);
			this.myDetailForm.get("localizacionID").updateValueAndValidity();

			this.myDetailForm
				.get("ciudad")
				.removeValidators(Validators.required);
			this.myDetailForm.get("ciudad").updateValueAndValidity();
		}
		this._loadData();

		this.parent.loadCebesCentro();
	}

	ngAfterContentChecked(): void {
		this.getFormStatus();
	}

	loadCentroDetailsFormGroup() {
		this.myDetailForm = this.fb.group({
			descripcion: [this.centro?.descripcion, [Validators.required]],
			tipoInstalacionID: [
				this.centro?.tipoInstalacionID,
				[Validators.required],
			],
			latitud: [this.centro?.latitud ?? null, [Validators.required]],
			longitud: [this.centro?.longitud ?? null, [Validators.required]],
			ciudad: [this.centro?.ciudad],
			centroActividadID: [this.centro?.centroActividadID, []],
			localizacionID: [this.centro?.localizacionID],
			activo: [this.centro?.activo ? this.centro?.activo : false],
			imageDataB64: [this.centro?.imageDataB64],
		});

		this.centroDetailData.emit(this.myDetailForm);

		this.myDetailForm
			.get("tipoInstalacionID")
			.valueChanges.subscribe((s) => {
				this.parent.showHideInstallationFields();
			});

		this.myDetailForm.valueChanges.subscribe(() => {
			this.getFormStatus();

			this.centroDetailData.emit(this.myDetailForm);
		});
	}

	get f(): { [key: string]: AbstractControl } {
		return this.myDetailForm.controls;
	}

	getFormStatus() {
		this.countControls = 0;
		this.countFilled = 0;

		Object.keys(this.myDetailForm.controls).forEach((controlName) => {
			const control = this.myDetailForm.get(controlName);

			const controlElement = document.querySelector(
				`[formControlName="${controlName}"]`
			);

			if (controlElement) {
				const hasHiddenClass =
					controlElement.parentElement.classList.contains("hidden");

				if (!hasHiddenClass) {
					this.countControls++;

					if (control?.value !== null && control?.value !== "") {
						this.countFilled++;
					}
				}
			}
		});

		if (this.countFilled !== 0) {
			this.detailFormComplete.emit(
				this.countControls === this.countFilled
			);
		}
	}

	private _loadData(): void {
		forkJoin([
			this._masterService.getTipoInstalacionList(),
			this._masterService.getCentroActividadList(),
			this._masterService.getLocalizacionList(),
		]).subscribe(([s1, s2, s3]) => {
			this.alltipoInstalacionList = s1;
			this.centroActividadList = s2;
			this.localizacionList = s3;

			this.alltipoInstalacionList.forEach((inst) => {
				if (
					this.cebe?.tipoProyectoID === inst.tipoProyectoID &&
					this.cebe?.sociedadID === inst.sociedadID &&
					this.cebe?.actividadID === inst.actividadID
				) {
					const instIds = this.tipoInstalacionList.map((it) => it.id);
					if (instIds.indexOf(inst.id) === -1) {
						this.tipoInstalacionList.push(inst);
					}
				}
			});

			this.parent.hideAllOperacionDisenoFields();
			this.parent.initFieldsAndActivitySelector();
		});
		this.imagenPreview = this.centro?.imageDataB64;
	}

	Migrar() {
		this._centrosService.Migrar(this.centro?.id).subscribe((s) => {
			this._notificationService.showSuccess(
				this._translateService.instant("messages.savedSuccessfully"),
				null
			);
			this.parent.ngOnInit();
		});
	}

	Validate() {
		this._centrosService.Validate(this.centro?.id).subscribe((s) => {
			this._notificationService.showSuccess(
				this._translateService.instant("messages.savedSuccessfully"),
				null
			);
			this.parent.ngOnInit();
		});
	}

	onFileChange(event: Event) {
		const input = event.target as HTMLInputElement;
		const file = input.files && input.files[0];
		const fileSizeKb = file.size / 1024;

		if (
			file &&
			(file.type === "image/jpeg" || file.type === "image/png") &&
			fileSizeKb <= 1000
		) {
			this.imagenFile = file;

			const reader = new FileReader();
			reader.onload = () => {
				const base64Image = reader.result as string;
				this.myDetailForm.patchValue({ imageDataB64: base64Image });
				this.myDetailForm.get("imageDataB64")?.updateValueAndValidity();
				this.imagenPreview = base64Image;
			};

			reader.readAsDataURL(file);
		} else {
			this.myDetailForm.patchValue({ imageDataB64: null });
			this.imagenFile = null;
			this.imagenPreview = null;
			input.value = "";
			if (fileSizeKb > 1000) {
				alert(
					this._translateService.instant("messages.imageRequiredType")
				);
			}
		}
	}
}
